import { create } from 'zustand';
import { toast } from 'react-hot-toast';
import { logger } from '@/features/help/utils/logger';
import api from '@/features/api/axios';

interface NotificationSettings {
  notificationEmail: string | null;
  notifyCrmOwner: boolean;
}

interface UserSettings {
  attachAnalysisPdf?: boolean;
  // Add other future settings here
}

interface SettingsState {
  // Notification settings
  notificationEmail: string | null;
  notifyCrmOwner: boolean;

  // UI States
  isLoading: boolean;
  isSaving: boolean;
  error: string | null;

  // Actions
  fetchSettings: () => Promise<void>;
  setNotificationEmail: (email: string | null) => void;
  setNotifyCrmOwner: (notify: boolean) => void;
  updateSettings: (settings: Partial<NotificationSettings>) => Promise<boolean>;

  settings: UserSettings;
  updateSetting: (key: keyof UserSettings, value: any) => Promise<boolean>;
}

/**
 * Store for user settings including notification preferences
 */
export const useSettingsStore = create<SettingsState>((set, get) => ({
  // Initial state
  notificationEmail: null,
  notifyCrmOwner: false,
  isLoading: false,
  isSaving: false,
  error: null,

  settings: {
    // Initial default state
    attachAnalysisPdf: true,
  },

  /**
   * Fetches user settings from the API
   */
  fetchSettings: async () => {
    try {
      set({ isLoading: true, error: null });
      logger.info('SettingsFlow', 'Fetching user settings');

      const response = await api.get('/api/email/notifications/settings');

      logger.info('SettingsFlow', 'Settings fetched successfully', { data: response.data });

      set({
        notificationEmail: response.data.notificationEmail,
        notifyCrmOwner: response.data.notifyCrmOwner || false,
        isLoading: false,
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      logger.error('SettingsFlow', 'Error fetching settings', { error });
      set({ error: errorMessage, isLoading: false });
      toast.error('Failed to load settings');
    }
  },

  /**
   * Sets the notification email in the store
   */
  setNotificationEmail: (email) => {
    set({ notificationEmail: email });
  },

  /**
   * Sets the notify CRM owner preference in the store
   */
  setNotifyCrmOwner: (notify) => {
    set({ notifyCrmOwner: notify });
  },

  /**
   * Updates user settings via the API
   */
  updateSettings: async (settings) => {
    try {
      set({ isSaving: true, error: null });
      logger.info('SettingsFlow', 'Updating user settings', { settings });

      const response = await api.put('/api/email/notifications/settings', settings);

      logger.info('SettingsFlow', 'Settings updated successfully', { data: response.data });

      set({
        ...settings,
        isSaving: false,
      });

      toast.success('Settings saved successfully');
      return true;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      logger.error('SettingsFlow', 'Error updating settings', { error });
      set({ error: errorMessage, isSaving: false });
      toast.error('Failed to save settings');
      return false;
    }
  },

  /**
   * Updates a specific user setting via the backend API.
   * @param key The key of the setting to update (e.g., 'attachAnalysisPdf')
   * @param value The new value for the setting.
   * @returns True if the update was successful, false otherwise.
   */
  updateSetting: async (key: keyof UserSettings, value: any): Promise<boolean> => {
    // Map frontend key to backend key if needed (assuming camelCase -> snake_case)
    const backendKeyMap: Record<keyof UserSettings, string> = {
      attachAnalysisPdf: 'attach_analysis_pdf',
    };
    const backendKey = backendKeyMap[key];

    if (!backendKey) {
      logger.error('SettingsStore', 'Attempted to update unknown setting key', { key });
      return false;
    }

    set({ isLoading: true, error: null }); // Indicate loading state for update
    const currentSettings = get().settings;

    try {
      logger.debug('SettingsStore', 'Attempting to update setting', { key, value, backendKey });
      await api.put(`/api/ai/settings/${backendKey}`, { value }); // Use correct backend route

      // Update local state on success
      const updatedSettings = { ...currentSettings, [key]: value };
      set({ settings: updatedSettings, isLoading: false });
      logger.info('SettingsStore', 'Setting updated successfully.', { key, value });
      return true;
    } catch (error: any) {
      logger.error('SettingsStore', 'Failed to update setting.', {
        key,
        value,
        backendKey,
        errorMessage: error.message,
        responseData: error.response?.data,
      });
      // Revert local state on error? Optional, depends on desired UX.
      // set({ settings: currentSettings, isLoading: false, error: `Failed to update ${key}.` });
      set({ isLoading: false, error: `Failed to update analysis settings. Please try again.` });
      return false;
    }
  },
}));
